// IdCardComponents.tsx
// Pure card rendering components + print HTML builder
// Portrait design (circular photo, diagonal header, STUDENT ribbon, barcode)

import React, { useEffect, useRef } from 'react';
import QRCode from 'qrcode';

// ─── Constants ───────────────────────────────────────────────────────────────
export const navy = '#0e225d';
export const gold  = '#fbb21f';

// Screen preview size — portrait CR80-ish card
export const CARD_W = 350;
export const CARD_H = 570;

// ─── Types ───────────────────────────────────────────────────────────────────
export interface BatchInfo {
  batch_id:     string;
  batch_name:   string;
  batch_time:   string;
  course_name:  string | null;
  course_title: string | null;
  is_active:    boolean;
  valid_till?:  string | null;
}

export interface Student {
  student_id:             string;
  enrollment_id:          string;
  name:                   string;
  dateOfBirth:            string | null;
  gender:                 string | null;
  mobileNumber:           string | null;
  fatherName:             string | null;
  motherName:             string | null;
  district:               string | null;
  state:                  string | null;
  villageTown:            string | null;
  profilePicture:         { cdn_url?: string } | null;
  status:                 string;
  date_of_admission:      string;
  is_registered:          boolean;
  category:               string | null;
  bloodGroup:             string | null;
  emergencyContactName?:  string | null;
  emergencyContactPhone?: string | null;
  validTill?:             string | null;
  student_custom_id?:     string | null;
}

export interface CardProps {
  student:       Student;
  batchInfo:     BatchInfo | null;
  schoolName:    string;
  schoolAddress: string;
  websiteUrl:    string;
  validTill?:    string | null;
  branchId?:     string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
export const getInitials = (name: string) => {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const parseDate = (raw: string | null | undefined): Date | null => {
  if (!raw) return null;
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

export const formatDate = (raw: string | null | undefined): string => {
  const d = parseDate(raw);
  if (!d) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDOB = formatDate;

// ─── Subtle diagonal stripe pattern ──────────────────────────────────────────
const DiagPattern: React.FC<{ id: string; light?: boolean }> = ({ id, light }) => (
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"
    style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: light ? 0.5 : 0.4 }}>
    <defs>
      <pattern id={id} patternUnits="userSpaceOnUse" width="20" height="20" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="20"
          stroke={light ? 'rgba(14,34,93,0.07)' : 'rgba(251,178,31,0.14)'}
          strokeWidth="1.2" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#${id})`} />
  </svg>
);

// ─── Shared card shell ────────────────────────────────────────────────────────
const cardShell: React.CSSProperties = {
  width:         CARD_W,
  height:        CARD_H,
  borderRadius:  22,
  overflow:      'hidden',
  boxShadow:     '0 20px 60px rgba(14,34,93,0.22), 0 4px 16px rgba(14,34,93,0.12)',
  fontFamily:    "'Plus Jakarta Sans', sans-serif",
  display:       'flex',
  flexDirection: 'column',
  background:    '#fff',
  flexShrink:    0,
  position:      'relative',
};

// ─── Diagonal header (shared by front/back) ──────────────────────────────────
const DiagonalHeader: React.FC<{
  patId:         string;
  schoolName:    string;
  schoolAddress: string;
  rightLabel?:   string;
  rightValue?:   string;
  height?:       number;
}> = ({ patId, schoolName, schoolAddress, rightLabel, rightValue, height = 128 }) => (
  <div style={{
    position:  'relative',
    height,
    flexShrink: 0,
    background: 'linear-gradient(100deg, #071640 0%, #0e225d 55%, #1a3a7a 100%)',
    clipPath:  'polygon(0 0, 100% 0, 100% 76%, 0 100%)',
    overflow:  'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: rightLabel ? 'space-between' : 'center',
    padding: '16px 18px 12px',
  }}>
    <DiagPattern id={patId} />
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${gold}, #ffdc71, ${gold})`, zIndex: 3 }} />

    <div style={{ position: 'relative', zIndex: 3, display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%', background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', flexShrink: 0,
        boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
      }}>
        <img src="https://hciindia.in/static/images/logo/new-hci-logo.avif" alt="Logo"
          style={{ width: '84%', height: '84%', objectFit: 'contain' }} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: 15, fontWeight: 800, color: '#fff', lineHeight: 1.25, letterSpacing: '-0.2px',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {schoolName}
        </div>
        <div style={{
          fontSize: 10.5, color: 'rgba(255,255,255,0.82)', marginTop: 2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {schoolAddress}
        </div>
      </div>
    </div>

    {rightLabel && (
      <div style={{ position: 'relative', zIndex: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 5, marginTop: 6 }}>
        <span style={{ fontSize: 8.5, fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {rightLabel}
        </span>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: gold, whiteSpace: 'nowrap' }}>
          {rightValue}
        </span>
      </div>
    )}
  </div>
);

// ─── Info row (icon + label + value) ─────────────────────────────────────────
const InfoRow: React.FC<{ icon: string; label: string; value: string }> = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
    <div style={{
      width: 24, height: 24, borderRadius: 6, background: navy, flexShrink: 0, marginTop: 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <i className={`pi ${icon}`} style={{ fontSize: 11, color: '#fff' }} />
    </div>
    <div style={{ display: 'flex', minWidth: 0, flex: 1, gap: 6 }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#6b7280', flexShrink: 0, width: 92 }}>{label}</div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: '#374151' }}>:&nbsp;{value}</div>
    </div>
  </div>
);

// ─── FRONT CARD ───────────────────────────────────────────────────────────────
export const IdCardFront: React.FC<CardProps> = ({
  student, batchInfo, schoolName, schoolAddress, websiteUrl, validTill, branchId,
}) => {
  const resolvedValidTill = validTill ?? student.validTill ?? batchInfo?.valid_till ?? null;
  const pid = `fp-${student.student_id}`;
  const enrollmentId = student.student_custom_id || student.enrollment_id || student.student_id.slice(-6).toUpperCase();
  const address = [student.villageTown, student.district, student.state].filter(Boolean).join(', ') || '—';

  return (
    <div style={cardShell}>
      <DiagonalHeader
        patId={pid} schoolName={schoolName} schoolAddress={schoolAddress}
        rightLabel="ID No." rightValue={enrollmentId} height={150}
      />

      {/* Photo overlapping the header */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: -74, position: 'relative', zIndex: 5 }}>
        <div style={{
          width: 132, height: 132, borderRadius: '50%', border: '5px solid #fff',
          boxShadow: '0 8px 24px rgba(14,34,93,0.28)', overflow: 'hidden',
          background: gold, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {student.profilePicture?.cdn_url
            ? <img src={student.profilePicture.cdn_url} alt={student.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
            : <span style={{ fontSize: 40, fontWeight: 800, color: navy }}>{getInitials(student.name)}</span>}
        </div>
      </div>

      {/* STUDENT ribbon */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
        <div style={{
          background: navy, color: '#fff', padding: '5px 26px', borderRadius: 20,
          fontWeight: 800, fontSize: 12, letterSpacing: '1.5px',
        }}>
          STUDENT
        </div>
      </div>

      {/* Name */}
      <div style={{ textAlign: 'center', fontSize: 19, fontWeight: 800, color: navy, marginTop: 10, padding: '0 16px' }}>
        {student.name}
      </div>

      {/* Info rows */}
      <div style={{ padding: '16px 24px 0', display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        <InfoRow icon="pi-user" label="Father's Name" value={student.fatherName || '—'} />
        <InfoRow icon="pi-calendar" label="Date of Birth" value={formatDOB(student.dateOfBirth)} />
        <InfoRow icon="pi-book" label="Batch" value={batchInfo?.batch_name || '—'} />
        <InfoRow icon="pi-id-card" label="Admission No." value={enrollmentId} />
        <InfoRow icon="pi-map-marker" label="Address" value={address} />
      </div>

      {/* Signature + Valid Upto */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', padding: '10px 22px 20px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ height: 18 }} />
          <div style={{ width: 84, height: 1, background: navy, opacity: 0.35, margin: '0 auto' }} />
          <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: 4 }}>
            Issuing Authority
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 26, height: 26, borderRadius: '50%', background: 'rgba(251,178,31,0.15)',
            border: `1.5px solid ${gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className="pi pi-shield" style={{ fontSize: 12, color: '#c98a00' }} />
          </div>
          <div>
            <div style={{ fontSize: 8.5, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>Valid Upto</div>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: navy }}>{formatDate(resolvedValidTill)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── BACK CARD ────────────────────────────────────────────────────────────────
export const IdCardBack: React.FC<CardProps> = ({
  student, batchInfo, schoolName, schoolAddress, websiteUrl, validTill, branchId,
}) => {
  const qrRef = useRef<HTMLCanvasElement>(null);
  const pid   = `bp-${student.student_id}`;

  const qrUrl = branchId
    ? `http://hciindia.in/details/${branchId}`
    : `https://${websiteUrl}`;

  useEffect(() => {
    if (qrRef.current) {
      QRCode.toCanvas(qrRef.current, qrUrl, {
        width: 80, margin: 0, color: { dark: navy, light: '#ffffff' },
      });
    }
  }, [qrUrl]);

  const emergencyName  = student.emergencyContactName  || student.fatherName  || student.motherName || '—';
  const emergencyPhone = student.emergencyContactPhone || student.mobileNumber || '—';

  return (
    <div style={cardShell}>
      <DiagonalHeader patId={pid} schoolName={schoolName} schoolAddress={schoolAddress} height={90} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '18px 22px', gap: 14 }}>
        {/* Emergency */}
        <div style={{
          background: 'rgba(14,34,93,0.03)', border: '1px solid rgba(14,34,93,0.09)',
          borderRadius: 10, padding: '12px 14px',
        }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: navy, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
            Emergency Contact
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Name</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: navy, marginTop: 2 }}>{emergencyName}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Phone</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: navy, marginTop: 2 }}>{emergencyPhone}</div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div style={{
          background: 'rgba(251,178,31,0.06)', border: '1px solid rgba(251,178,31,0.22)',
          borderRadius: 8, padding: '10px 14px',
        }}>
          {[
            'This ID card is the property of the institute and must be carried at all times within the premises.',
            'It is strictly non-transferable.',
            'If found, please return to: Hindustan Computer Institute.',
          ].map((n, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: i < 2 ? 5 : 0 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: gold, marginTop: 6, flexShrink: 0 }} />
              <div style={{ fontSize: 10, color: '#374151', lineHeight: 1.5 }}>{n}</div>
            </div>
          ))}
        </div>

        {/* QR + signature */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: navy, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8, opacity: 0.7 }}>
              Scan to Verify
            </div>
            <div style={{
              width: 100, height: 100, border: `2px solid ${gold}`,
              borderRadius: 12, overflow: 'hidden', background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto', boxShadow: '0 4px 16px rgba(14,34,93,0.12)',
            }}>
              <canvas ref={qrRef} />
            </div>
          </div>
          <div style={{ textAlign: 'center', width: '100%', borderTop: '1px solid rgba(14,34,93,0.12)', paddingTop: 12 }}>
            <div style={{ width: '60%', height: 1, background: navy, margin: '0 auto', opacity: 0.3 }} />
            <div style={{ fontSize: 9, fontWeight: 600, color: navy, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: 6 }}>
              Issuing Authority
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: 4, background: `linear-gradient(90deg, ${navy}, ${gold}, ${navy})`, flexShrink: 0 }} />
    </div>
  );
};

// ─── PRINT HTML BUILDER ───────────────────────────────────────────────────────
const diagPatternSvg = (id: string) => `
<svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style="position:absolute;inset:0;pointer-events:none;opacity:0.4">
  <defs>
    <pattern id="${id}" patternUnits="userSpaceOnUse" width="20" height="20" patternTransform="rotate(45)">
      <line x1="0" y1="0" x2="0" y2="20" stroke="rgba(251,178,31,0.14)" stroke-width="1.2"/>
    </pattern>
  </defs>
  <rect width="100%" height="100%" fill="url(#${id})"/>
</svg>`;

const fmtPrint = (raw: string | null | undefined): string => {
  if (!raw) return '—';
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const headerHTML = (pid: string, schoolName: string, schoolAddress: string, rightLabel?: string, rightValue?: string, cls = 'hdr') => `
<div class="${cls}${rightLabel ? ' hdr-tall' : ''}">
  ${diagPatternSvg(pid)}
  <div class="gold-bar-top"></div>
  <div class="hdr-left">
    <div class="logo-ring"><img src="https://hciindia.in/static/images/logo/new-hci-logo.avif" class="logo-img" alt=""/></div>
    <div class="hdr-text">
      <div class="hdr-name">${schoolName}</div>
      <div class="hdr-addr">${schoolAddress}</div>
    </div>
  </div>
  ${rightLabel ? `<div class="hdr-idrow"><span class="hdr-idrow-lbl">${rightLabel}</span><span class="hdr-idrow-val">${rightValue}</span></div>` : ''}
</div>`;

// icon class for each info row on the printed front card
const frontCardHTML = (
  s: Student,
  batchInfo: BatchInfo | null,
  schoolName: string,
  schoolAddress: string,
  validTill: string,
): string => {
  const vt  = fmtPrint(validTill || s.validTill || batchInfo?.valid_till || null);
  const pid = `pf-${s.student_id}`;
  const initials = getInitials(s.name);
  const enrollId = s.student_custom_id || s.enrollment_id || s.student_id.slice(-6).toUpperCase();
  const address = [s.villageTown, s.district, s.state].filter(Boolean).join(', ') || '—';

  // [label, value, icon-class] — icon-class matches the on-screen InfoRow icons
  const rows: [string, string, string][] = [
    ["Father's Name", s.fatherName || '—', 'pi-user'],
    ['Date of Birth', fmtPrint(s.dateOfBirth), 'pi-calendar'],
    ['Batch',         batchInfo?.batch_name || '—', 'pi-book'],
    ['Admission No.', enrollId, 'pi-id-card'],
    ['Address',       address, 'pi-map-marker'],
  ];

  return `
<div class="card front">
  ${headerHTML(pid, schoolName, schoolAddress, 'ID No.', enrollId)}
  <div class="photo-wrap-outer">
    <div class="photo-circ">
      ${s.profilePicture?.cdn_url
        ? `<img src="${s.profilePicture.cdn_url}" class="photo-img" alt="${s.name}"/>`
        : `<span class="photo-init">${initials}</span>`}
    </div>
  </div>
  <div class="student-ribbon"><span>STUDENT</span></div>
  <div class="f-name">${s.name}</div>
  <div class="f-rows">
    ${rows.map(([l, v, ic]) => `<div class="f-row"><div class="f-ic"><i class="pi ${ic}"></i></div><div class="f-lbl">${l}</div><div class="f-val">:&nbsp;${v}</div></div>`).join('')}
  </div>
  <div class="f-footer">
    <div class="f-sig"><div class="sig-line-sm"></div><div class="sig-lbl-sm">Issuing Authority</div></div>
    <div class="f-valid"><div class="valid-badge"><i class="pi pi-shield"></i></div><div><div class="valid-lbl">Valid Upto</div><div class="valid-val">${vt}</div></div></div>
  </div>
  <div class="gold-bar-bottom"></div>
</div>`;
};

const backCardHTML = (
  s: Student,
  batchInfo: BatchInfo | null,
  schoolName: string,
  schoolAddress: string,
  qrUrl: string,
): string => {
  const pid        = `pb-${s.student_id}`;
  const emergName  = s.emergencyContactName  || s.fatherName  || s.motherName || '—';
  const emergPhone = s.emergencyContactPhone || s.mobileNumber || '—';

  return `
<div class="card back">
  ${headerHTML(pid, schoolName, schoolAddress, undefined, undefined, 'hdr hdr-short')}
  <div class="b-body">
    <div class="emerg">
      <div class="sec-hd"><i class="pi pi-phone"></i> Emergency Contact</div>
      <div class="emerg-row">
        <div><div class="lbl">Name</div><div class="val">${emergName}</div></div>
        <div class="tr"><div class="lbl">Phone</div><div class="val">${emergPhone}</div></div>
      </div>
    </div>
    <div class="notes">
      <div class="note">Property of the institute. Must be carried in premises.</div>
      <div class="note">Non-transferable.</div>
      <div class="note">If found, return to: Hindustan Computer Institute.</div>
    </div>
    <div class="b-bottom">
      <div class="qr-lbl">Scan to Verify</div>
      <div class="qr-box" id="qr-${s.student_id}"></div>
      <div class="sig">
        <div class="sig-line"></div>
        <div class="sig-lbl">Issuing Authority</div>
      </div>
    </div>
  </div>
  <div class="gold-bar-bottom"></div>
</div>`;
};

export const buildPrintHTML = (
  cards:         Student[],
  batchInfo:     BatchInfo | null,
  schoolName:    string,
  schoolAddress: string,
  websiteUrl:    string,
  validTill:     string,
  branchId?:     string | null,
): string => {
  const qrUrl = branchId
    ? `http://hciindia.in/details/${branchId}`
    : `https://${websiteUrl}`;

  let pages = '';
  for (let i = 0; i < cards.length; i += 3) {
    const slice = cards.slice(i, i + 3);
    pages += `<div class="page">${slice.map(s =>
      `<div class="pair">
        ${frontCardHTML(s, batchInfo, schoolName, schoolAddress, validTill)}
        ${backCardHTML(s, batchInfo, schoolName, schoolAddress, qrUrl)}
      </div>`
    ).join('')}</div>`;
  }

  const css = `
    *{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    body{font-family:'Plus Jakarta Sans',sans-serif;background:#dde4f0}

    .page{width:210mm;margin:0 auto;padding:5mm 8mm;display:flex;flex-direction:column;gap:4mm;page-break-after:always;break-after:page;page-break-inside:avoid}

    .pair{display:flex;flex-direction:row;gap:5mm;align-items:flex-start;justify-content:center;page-break-inside:avoid;break-inside:avoid}

    /* CR80 card size, height nudged up slightly to guarantee content never clips at the bottom */
    .card{width:54mm;height:87mm;background:#fff !important;border-radius:4mm;overflow:hidden;box-shadow:0 2mm 6mm rgba(14,34,93,0.15);position:relative;flex-shrink:0;display:flex;flex-direction:column;page-break-inside:avoid;break-inside:avoid}

    .gold-bar-top{position:absolute;top:0;left:0;right:0;height:0.9mm;background:linear-gradient(90deg,#fbb21f,#ffdc71,#fbb21f) !important;z-index:5}
    .gold-bar-bottom{height:0.9mm;background:linear-gradient(90deg,#0e225d,#fbb21f,#0e225d) !important;flex-shrink:0}

    .hdr{position:relative;height:20mm;flex-shrink:0;background:linear-gradient(100deg,#071640 0%,#0e225d 55%,#1a3a7a 100%) !important;clip-path:polygon(0 0,100% 0,100% 76%,0 100%);overflow:hidden;display:flex;flex-direction:column;justify-content:center;padding:3mm 3mm 2mm}
    .hdr-tall{height:24mm;justify-content:space-between}
    .hdr-short{height:14mm}
    .hdr-left{position:relative;z-index:3;display:flex;align-items:center;gap:2mm}
    .logo-ring{width:8mm;height:8mm;border-radius:50%;background:#fff !important;display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0;box-shadow:0 0.5mm 2mm rgba(0,0,0,0.22)}
    .logo-img{width:84%;height:84%;object-fit:contain}
    .hdr-text{min-width:0;flex:1}
    .hdr-name{font-size:7pt;font-weight:800;color:#fff;line-height:1.25;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .hdr-addr{font-size:5pt;color:rgba(255,255,255,0.82);margin-top:0.4mm;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .hdr-idrow{position:relative;z-index:3;display:flex;justify-content:flex-end;align-items:baseline;gap:1mm;margin-top:1.5mm}
    .hdr-idrow-lbl{font-size:4pt;font-weight:700;color:rgba(255,255,255,0.65);text-transform:uppercase}
    .hdr-idrow-val{font-size:5.5pt;font-weight:800;color:#fbb21f;white-space:nowrap}

    .photo-wrap-outer{display:flex;justify-content:center;margin-top:-12mm;position:relative;z-index:5}
    .photo-circ{width:20mm;height:20mm;border-radius:50%;border:0.9mm solid #fff;box-shadow:0 1.5mm 4mm rgba(14,34,93,0.28);overflow:hidden;background:#fbb21f !important;display:flex;align-items:center;justify-content:center}
    .photo-img{width:100%;height:100%;object-fit:cover;object-position:center top}
    .photo-init{font-size:16pt;font-weight:800;color:#0e225d}

    .student-ribbon{display:flex;justify-content:center;margin-top:1mm}
    .student-ribbon span{background:#0e225d !important;color:#fff;padding:0.6mm 4mm;border-radius:3mm;font-weight:800;font-size:5.5pt;letter-spacing:0.4pt}

    .f-name{text-align:center;font-size:8.5pt;font-weight:800;color:#0e225d;margin-top:1mm;padding:0 2mm}

    .f-rows{padding:1.5mm 3.5mm 0;display:flex;flex-direction:column;gap:1.2mm;flex:1}
    .f-row{display:flex;align-items:flex-start;gap:1.5mm;font-size:5.2pt}
    .f-ic{width:3mm;height:3mm;border-radius:0.8mm;background:#0e225d !important;flex-shrink:0;margin-top:0.2mm;display:flex;align-items:center;justify-content:center}
    .f-ic i{font-size:4pt;color:#fff}
    .f-lbl{font-weight:700;color:#6b7280;width:15mm;flex-shrink:0}
    .f-val{font-weight:600;color:#374151}

    .f-footer{display:flex;justify-content:space-between;align-items:flex-end;padding:2mm 3mm 1.5mm}
    .sig-line-sm{width:14mm;height:0.25mm;background:#0e225d;opacity:0.35;margin:1.5mm 0 0.6mm}
    .sig-lbl-sm{font-size:4pt;color:#9ca3af;font-weight:700;text-transform:uppercase;text-align:center}
    .f-valid{display:flex;align-items:center;gap:1mm}
    .valid-badge{width:4mm;height:4mm;border-radius:50%;background:rgba(251,178,31,0.15) !important;border:0.3mm solid #fbb21f;display:flex;align-items:center;justify-content:center}
    .valid-badge i{font-size:4.5pt;color:#c98a00}
    .valid-lbl{font-size:4pt;color:#9ca3af;font-weight:700;text-transform:uppercase}
    .valid-val{font-size:5.5pt;font-weight:800;color:#0e225d}

    .b-body{flex:1;display:flex;flex-direction:column;padding:2mm 3.5mm;gap:2mm;overflow:hidden}
    .sec-hd{font-size:4.5pt;font-weight:800;color:#0e225d;text-transform:uppercase;letter-spacing:0.3pt;margin-bottom:1mm;display:flex;align-items:center;gap:1mm}
    .sec-hd i{font-size:4.5pt}
    .emerg{background:rgba(14,34,93,0.03) !important;border-radius:1.5mm;padding:1.5mm 2.5mm;border:0.3mm solid rgba(14,34,93,0.09)}
    .emerg-row{display:flex;justify-content:space-between;gap:3mm}
    .lbl{font-size:4pt;font-weight:600;color:#9ca3af;text-transform:uppercase}
    .val{font-size:5.5pt;font-weight:700;color:#0e225d;margin-top:0.3mm}
    .tr{text-align:right}
    .notes{background:rgba(251,178,31,0.06) !important;border-radius:1.2mm;padding:1.5mm 2.5mm;border:0.3mm solid rgba(251,178,31,0.25)}
    .note{font-size:4pt;color:#374151;line-height:1.6;padding-left:2.5mm;position:relative;margin-bottom:0.3mm}
    .note::before{content:"•";position:absolute;left:0;color:#fbb21f;font-weight:800}
    .b-bottom{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:flex-end;gap:2mm}
    .qr-lbl{font-size:4.5pt;font-weight:700;color:rgba(14,34,93,0.7);text-transform:uppercase;text-align:center}
    .qr-box{width:16mm;height:16mm;border:0.6mm solid #fbb21f;border-radius:2mm;overflow:hidden;background:#fff !important;display:flex;align-items:center;justify-content:center;box-shadow:0 1mm 3mm rgba(14,34,93,0.12)}
    .sig{text-align:center;width:100%;border-top:0.3mm solid rgba(14,34,93,0.12);padding-top:1.2mm}
    .sig-line{width:60%;height:0.3mm;background:rgba(14,34,93,0.3);margin:0 auto}
    .sig-lbl{font-size:4pt;font-weight:600;color:rgba(14,34,93,0.5);text-transform:uppercase;margin-top:1mm}

    @media print{
      html,body{margin:0;padding:0;background:#fff !important}
      .page{padding:5mm 8mm;gap:4mm}
      .card{box-shadow:none;border:0.3mm solid #d1d5db}
      @page{size:A4 portrait;margin:0}
    }
  `;

  return `<!DOCTYPE html><html lang="en"><head>
    <meta charset="UTF-8"/>
    <title>Student ID Cards</title>
    <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/primeicons/6.0.1/primeicons.min.css"/>
    <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"><\/script>
    <style>${css}</style>
    </head><body>
    ${pages}
    <script>
      window.onload = function() {
        document.querySelectorAll('[id^="qr-"]').forEach(function(el) {
          new QRCode(el, {
            text: "${qrUrl}",
            width: 50, height: 50,
            colorDark: "#0e225d", colorLight: "#ffffff",
            correctLevel: QRCode.CorrectLevel.M
          });
        });
        setTimeout(function(){ window.print(); }, 1200);
      };
    <\/script></body></html>`;
};